import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { BREAKPOINT_QUERIES } from 'styles/breakpoints'

export const Card = styled.article`
  background: #181818;
  border-radius: 3px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  padding: 8px;
  position: relative;
  width: 100%;

  &:focus-within {
    outline: 1px solid ${({ theme }) => theme.colors.player50};
    outline-offset: 2px;
  }

  @media ${BREAKPOINT_QUERIES.tabletUp} {
    border-radius: 5px;
    gap: 16px;
    padding: 16px;
    width: 214px;
  }

  @media ${BREAKPOINT_QUERIES.desktopUp} {
    background: transparent;

    &:hover,
    &:focus-within,
    &[data-actions-open='true'],
    &[data-context-menu-open='true'] {
      background: #181818;
    }
  }
`

export const CoverContainer = styled.div`
  aspect-ratio: 1;
  border-radius: 5px;
  overflow: hidden;
  position: relative;
  width: 100%;
  z-index: 2;

  @media ${BREAKPOINT_QUERIES.tabletUp} {
    width: 180px;
  }
`

export const CoverLink = styled(Link)`
  display: block;
  height: 100%;
  width: 100%;
`

export const PlayButton = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colors.neutralW100};
  border: 0;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.neutralW20};
  display: flex;
  height: 80px;
  justify-content: center;
  left: 50%;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  z-index: 2;

  &:disabled {
    cursor: default;
    opacity: 0;
  }

  @media (hover: hover) and (pointer: fine) {
    ${CoverContainer}:hover &:not(:disabled),
    ${CoverContainer}:focus-within &:not(:disabled) {
      opacity: 1;
      pointer-events: auto;
    }
  }

  &:focus-visible:not(:disabled) {
    opacity: 1;
    pointer-events: auto;
  }
`

export const CoverShade = styled.div`
  background: rgba(34, 34, 34, 0.6);
  inset: 0;
  opacity: 0;
  pointer-events: none;
  position: absolute;

  @media (hover: hover) and (pointer: fine) {
    ${CoverContainer}:hover &,
    ${CoverContainer}:focus-within & {
      opacity: 1;
    }
  }
`

export const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

export const SongCount = styled.p`
  color: ${({ theme }) => theme.colors.neutralW70};
  font-size: 12px;
  line-height: 16px;
  margin: 0;
`

export const TitleRow = styled.div`
  align-items: flex-end;
  display: flex;
  gap: 4px;
  min-width: 0;
`

export const Title = styled.h2`
  flex: 1;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  margin: 0;
  min-width: 0;

  @media ${BREAKPOINT_QUERIES.tabletUp} {
    font-size: 16px;
    line-height: 24px;
  }
`

export const TitleText = styled.span`
  position: relative;
  z-index: 2;

  &:hover {
    text-decoration: underline;
  }
`

export const TitleLink = styled(Link)`
  color: ${({ theme }) => theme.colors.neutralW90};
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;

  &::after {
    content: '';
    inset: 0;
    position: absolute;
    z-index: 1;
  }

  &:focus-visible ${TitleText} {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: none;
  }

  @media ${BREAKPOINT_QUERIES.tabletUp} {
    display: block;
    white-space: nowrap;
  }
`

export const MoreActions = styled.div`
  flex: 0 0 12px;
  opacity: 1;
  position: relative;
  z-index: 2;

  button {
    border-radius: 3px;
    height: 16px;
    position: relative;
    width: 12px;

    &::after {
      content: '';
      inset: -8px -10px;
      position: absolute;
    }
  }

  svg {
    height: 12px;
    width: 12px;
  }

  @media ${BREAKPOINT_QUERIES.tabletUp} {
    flex-basis: 18px;

    button {
      height: 24px;
      width: 18px;
    }

    svg {
      height: 18px;
      width: 18px;
    }
  }

  @media (hover: hover) and (pointer: fine) {
    opacity: ${({ $alwaysVisible, $isOpen }) =>
      $alwaysVisible || $isOpen ? 1 : 0};

    ${Card}:hover &,
    ${Card}:focus-within & {
      opacity: 1;
    }
  }
`
